import {
  getMonth,
  addMonths,
  getYear,
  subDays,
  differenceInDays,
  subYears,
  getWeek,
  getDay,
  setWeek,
  setDay,
} from 'date-fns';
import { IntlShape } from 'react-intl';
import type { DateRange, PresetOption } from '../types';

interface ComparisonDateRange {
  from?: Date;
  to?: Date;
}

export const getComparisonRange = (from?: Date, to?: Date, presetValue?: string): ComparisonDateRange => {
  const comparisonRange: ComparisonDateRange = {};

  if (!from || !to) return comparisonRange;

  const daysDiff = Math.abs(differenceInDays(from, to)) + 1;
  switch (presetValue) {
    case 'previous_period':
      comparisonRange.from = subDays(from, daysDiff);
      comparisonRange.to = subDays(to, daysDiff);
      break;

    case 'previous_year':
    case 'previous_year_by_date':
      comparisonRange.from = subYears(from, 1);
      comparisonRange.to = subYears(to, 1);
      break;

    case 'previous_year_by_cal_week': {
      const fromWeek = getWeek(from);
      const fromDayInWeek = getDay(from);
      const toWeek = getWeek(to);
      const toDayInWeek = getDay(to);

      comparisonRange.from = setDay(setWeek(subYears(from, 1), fromWeek), fromDayInWeek);
      comparisonRange.to = setDay(setWeek(subYears(to, 1), toWeek), toDayInWeek);
      break;
    }

    case 'custom':
    case 'no_comparison':
    default:
      break;
  }

  return comparisonRange;
};

export const getRangeDisplayName = (
  range: DateRange,
  options: {
    defaultValue: string;
    presets?: PresetOption[];
  },
  forceDates?: boolean | undefined,
  intl?: IntlShape
): string => {
  const preset = options.presets?.find(pre => pre.value === range.presetValue);

  const theLabel =
    !!preset?.label && intl ? intl.formatMessage({ id: preset.label, defaultMessage: preset.label }) : '';

  if (!forceDates && preset && preset.value !== 'custom') {
    return theLabel;
  }
  if (forceDates && preset && !range.from && !range.to) {
    return theLabel;
  }
  if (range.from && range.to) {
    return `${range.from.toLocaleDateString(intl?.locale ?? 'en_GB')} - ${range.to.toLocaleDateString(
      intl?.locale ?? 'en_GB'
    )}`;
  }

  return intl
    ? intl.formatMessage({
        id: options.defaultValue,
        defaultMessage: options.defaultValue,
      })
    : options.defaultValue;
};

function getFirstDay(date: Date): Date {
  const y = getYear(date);
  const m = getMonth(date);
  return new Date(y, m, 1);
}

function getLastDay(date: Date): Date {
  const y = getYear(date);
  const m = getMonth(date);
  return new Date(y, m + 1, 0);
}

export function isDateInView(date: Date, startMonthDate: Date, numberOfMonths = 2): boolean {
  const firstDay = getFirstDay(startMonthDate);
  const lastDay = getLastDay(addMonths(startMonthDate, numberOfMonths - 1));

  return firstDay <= date && date <= lastDay;
}
